import React, {
  useEffect, useRef, forwardRef, useImperativeHandle, useState,
} from "react";
import * as Sentry from "@sentry/react";

const TurnstileWrapper = forwardRef(
  ({
    onChange, captchaKey, setTurnstileInteractive, onError, action, emitNullOnReset,
  }, ref) => {
    const scriptSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(null);

    // expose reset to parent
    useImperativeHandle(ref, () => ({
      reset: () => {
        try {
          if (window.turnstile && widgetIdRef.current != null) {
            window.turnstile.reset(widgetIdRef.current);
            setTurnstileToken(null);
            if (emitNullOnReset) {
              onChange?.(null);
            }
            // we want it to be ready to interact again
            setTurnstileInteractive(true);
          }
        } catch (err) {
          console.warn("Turnstile reset failed:", err);
        }
      },
      // optional but useful if this component ever unmounts/remounts
      remove: () => {
        try {
          if (window.turnstile && widgetIdRef.current != null) {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = null;
          }
        } catch (err) {
          console.warn("Turnstile remove failed:", err);
        }
      },
    }));

    // load script (once)
    useEffect(() => {
      if (document.querySelector(`script[src='${scriptSrc}']`)) {
        setScriptLoaded(true);
        return;
      }

      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => onError?.(new Error("Failed to load Turnstile script"));
      document.head.appendChild(script);
    }, [onError]);

    // render widget once script + container are ready
    useEffect(() => {
      if (!scriptLoaded) {
        return;
      }
      if (!containerRef.current) {
        return;
      }
      if (!window.turnstile) {
        return;
      }
      if (widgetIdRef.current != null) {
        return;
      } // already rendered

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: captchaKey,
          action,
          appearance: "interaction-only",
          callback: (token) => {
            setTurnstileToken(token);
            onChange?.(token);
          },
          "expired-callback": () => {
            setTurnstileToken(null);
            onChange?.(null);
            setTurnstileInteractive(true);
          },
          "error-callback": (err) => onError?.(err),
          "before-interactive-callback": () => setTurnstileInteractive(true),
          "after-interactive-callback": () => setTurnstileInteractive(false),
          "unsupported-callback": (e = {}) => {
            Sentry.captureException(new Error("Browser does not support Turnstile"), {
              contexts: { data: { action, args: e } },
            });
          },
        });
      } catch (e) {
        onError?.(e);
      }
    }, [scriptLoaded, captchaKey, action, onChange, onError, setTurnstileInteractive]);

    return (
      <>
        <div ref={containerRef} />
        <input type="hidden" name="cf-turnstile-response" value={turnstileToken || ""} readOnly />
      </>
    );
  },
);

export default TurnstileWrapper;
